import IconFilter from '~/assets/images/magic-tags-icons/filter.svg?component-solid';
import IconGlobe from '~/assets/images/magic-tags-icons/globe.svg?component-solid';
import IconRequired from '~/assets/images/magic-tags-icons/required.svg?component-solid';
import { CustomFieldComponentType, EntityType } from '~/swagger/portal/Api';
import { Dict } from '~/utils/dict';
import LeasingIcon from './icons/LeasingIcon.svg?component-solid';
import ListingIcon from './icons/ListingIcon.svg?component-solid';
import MaintenanceIcon from './icons/MaintenanceIcon.svg?component-solid';
import PortfolioIcon from './icons/PortfolioIcon.svg?component-solid';
import PropertyIcon from './icons/PropertyIcon.svg?component-solid';
import TenantsIcon from './icons/TenantsIcon.svg?component-solid';
import UnitIcon from './icons/UnitIcon.svg?component-solid';
import UsersIcon from './icons/UsersIcon.svg?component-solid';
import AttachmentIcon from './tagIcons/attachment.svg?component-solid';
import CheckboxIcon from './tagIcons/checkbox.svg?component-solid';
import DateIcon from './tagIcons/date.svg?component-solid';
import DateTimeIcon from './tagIcons/dateTime.svg?component-solid';
import DescriptionIcon from './tagIcons/description.svg?component-solid';
import InputIcon from './tagIcons/input.svg?component-solid';
import NumberIcon from './tagIcons/number.svg?component-solid';
import SelectIcon from './tagIcons/select.svg?component-solid';
import type { Component, ComponentProps } from 'solid-js';
import type {
  CustomDateTimeFieldOptionsDto,
  CustomBooleanFieldOptionsDto,
  CustomNumberFieldOptionsDto,
  CustomReferenceFieldOptionsDto,
  CustomStringFieldOptionsDto,
  CustomFieldValueDto,
  EntityCustomFieldDto,
} from '~/swagger/portal/Api';

interface InUseEntity {
  icon: Component<ComponentProps<'svg'>>;
}

export const InUseEntityDict = new Dict<EntityType, InUseEntity>([
  { label: 'Portfolio', value: EntityType.Portfolio, meta: { icon: PortfolioIcon } },
  { label: 'Property', value: EntityType.Property, meta: { icon: PropertyIcon } },
  { label: 'Unit', value: EntityType.Unit, meta: { icon: UnitIcon } },
  { label: 'Lease', value: EntityType.Lease, meta: { icon: LeasingIcon } },
  { label: 'Vendor', value: EntityType.Vendor, meta: { icon: MaintenanceIcon } },
  { label: 'Owner', value: EntityType.Owner, meta: { icon: UsersIcon } },
  { label: 'Tenant', value: EntityType.Tenant, meta: { icon: TenantsIcon } },
  { label: 'Property manager', value: EntityType.PropertyManager, meta: { icon: UsersIcon } },
  { label: 'Unit Listing', value: EntityType.UnitListing, meta: { icon: ListingIcon } },
]);

export const SupportedReferenceEntityTypeDict = new Dict([{ label: 'File', value: EntityType.File }]);

export const magicTagTypeDict = new Dict<
  CustomFieldComponentType,
  {
    icon: Component<ComponentProps<'svg'>>;
    color: string;
    valueKey: (options?: EntityCustomFieldDto['options']) => keyof CustomFieldValueDto;
    optionsDefault: any;
  }
>([
  {
    label: 'Number',
    value: CustomFieldComponentType.Number,
    meta: {
      icon: NumberIcon,
      color: '#05AC3D',
      valueKey: () => 'numberValue',
      optionsDefault: {
        type: 'number',
        required: false,
      } as CustomNumberFieldOptionsDto,
    },
  },
  {
    label: 'Input',
    value: CustomFieldComponentType.Input,
    meta: {
      icon: InputIcon,
      color: '#3687E4',
      valueKey: () => 'stringValue',
      optionsDefault: {
        type: 'string',
        required: false,
      } as CustomStringFieldOptionsDto,
    },
  },
  {
    label: 'Textarea',
    value: CustomFieldComponentType.Textarea,
    meta: {
      icon: DescriptionIcon,
      color: '#4136E4',
      valueKey: () => 'stringValue',
      optionsDefault: {
        type: 'string',
        required: false,
        multiple: false,
        multiline: true,
      } as CustomStringFieldOptionsDto,
    },
  },
  {
    label: 'Select',
    value: CustomFieldComponentType.Select,
    meta: {
      icon: SelectIcon,
      color: '#F59E0B',
      valueKey: () => 'stringArray',
      optionsDefault: {
        type: 'string',
        required: false,
        multiple: true,
        multiline: false,
      } as CustomStringFieldOptionsDto,
    },
  },
  {
    label: 'File',
    value: CustomFieldComponentType.File,
    meta: {
      icon: AttachmentIcon,
      color: '#F37C32',
      valueKey: (options?: EntityCustomFieldDto['options']) => {
        const referenceOptions = options as CustomReferenceFieldOptionsDto;
        if (referenceOptions.multiple) {
          return 'referenceArray';
        }
        return 'referenceValue';
      },
      optionsDefault: {
        type: 'reference',
        required: false,
      } as CustomReferenceFieldOptionsDto,
    },
  },
  {
    label: 'Checkbox',
    value: CustomFieldComponentType.Checkbox,
    meta: {
      icon: CheckboxIcon,
      color: '#06B6D4',
      valueKey: () => 'booleanValue',
      optionsDefault: {
        type: 'boolean',
        required: false,
      } as CustomBooleanFieldOptionsDto,
    },
  },
  {
    label: 'Date',
    value: CustomFieldComponentType.Date,
    meta: {
      icon: DateIcon,
      color: '#EC4899',
      valueKey: () => 'dateTimeValue',
      optionsDefault: {
        type: 'datetime',
        required: false,
        dateOnly: true,
      } as CustomDateTimeFieldOptionsDto,
    },
  },
  {
    label: 'Date Time',
    value: CustomFieldComponentType.DateTime,
    meta: {
      icon: DateTimeIcon,
      color: '#F97316',
      valueKey: () => 'dateTimeValue',
      optionsDefault: {
        type: 'datetime',
        required: false,
        dateOnly: false,
      } as CustomDateTimeFieldOptionsDto,
    },
  },
]);

export const commonTagFieldsDict = new Dict<
  string,
  { icon: Component<ComponentProps<'svg'>>; color: string; filterKey?: 'filterable' | 'public'; filterAble?: boolean }
>([
  {
    label: 'Filterable tag',
    value: 'filterable',
    meta: {
      icon: IconFilter,
      color: '#424CFF',
      filterKey: 'filterable',
    },
  },
  {
    label: 'Visible to all users',
    value: 'public',
    meta: {
      icon: IconGlobe,
      color: '#0C8F24',
      filterKey: 'public',
    },
  },
  {
    label: 'Required field',
    value: 'options.required',
    meta: {
      icon: IconRequired,
      color: '#F94874',
      filterAble: false,
    },
  },
]);
